<template>
  <div class="copy-rotate text-dark">
    <button ref="copy_calendars_button"
            :class="{'do-bttn': activeCalendarDate}"
            @click.stop="toggleDropdown">Rotating schedule
    </button>
    <DModal v-if="isDropdown" :isBgOverlay="true" :showClose="false">
      <div slot="insertComponent">
        <div class="copy-calendars__panel">
          <div class="copy-rotate__header">
            <h3>Rotating schedule:
              <template v-if="!isSeeDates">Select days to copy</template>
              <template v-else>Select dates to insert</template>
            </h3>
            <div class="icon__bg-circle" @click="closeDropdown">
              <span class="icon-cross2"></span>
            </div>
          </div>
          <div class="copy-rotate__body">
            <div v-if="!isSeeDates">
              <Loader bg-color="#fff" v-if="isLoading || isLoadingSchoolSettings"></Loader>
              <template v-else>
              <div class="d-copy-input-group">
                <label class="d-label">
                  Number of days
                </label>
                <div class="d-number-picker">
                  <button @click="subtractDay">-</button>
                  <input type="text" disabled v-model="numberOfDays">
                  <button @click="addDay">+</button>
                </div>
              </div>
              <div>
                <ul class="copy-rotate__number-of-days">
                  <li v-for="(day, index) in daysToCopy" :key="index">
                    <p class="d-circle">{{ day.label }}</p>
                    <DateSelector
                      :date="day.date"
                      @changed="setDayToCopyDate($event, index)">
                    </DateSelector>
                  </li>
                </ul>
              </div>
              </template>
            </div>
            <CopyCalendarRotateDates
              v-if="isSeeDates"
              :initStartDate="initStartPasteDate"
              :schoolId="schoolId"
              :daysToCopy="daysToCopy"
              @pasteDates="setPasteDates">
            </CopyCalendarRotateDates>
          </div>
          <div class="copy-rotate__footer">
            <button v-if="isSeeDates" @click="isSeeDates = false">Back to number of days</button>
            <button v-if="!isSeeDates" @click="isSeeDates = true">See dates</button>
            <button class="bttn bttn-primary" v-if="isSeeDates" @click="submit">Submit days</button>
          </div>
        </div>
      </div>
    </DModal>
  </div>
</template>

<script>
import {mixin as clickaway} from 'vue-clickaway'
import moment from 'moment'
import DateSelector from '../datepicker/DateSelector'
import CopyCalendarRotateDates from './CopyCalendarRotateDates'
import DModal from '../common/DModal'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import Loader from '../common/Loader'

export default {
  components: {Loader, DModal, DateSelector, CopyCalendarRotateDates},
  mixins: [clickaway],
  props: {
    calendarId: {
      type: Number,
      default: null
    },
    activeCalendarDate: {
      default: null
    }
  },
  data () {
    return {
      isDropdown: false,
      numberOfDays: 5,
      daysToCopy: [],
      isSeeDates: false,
      pasteDates: [],
      sourceStartDate: moment(),
      isLoading: false
    }
  },
  computed: {
    activeSchoolYears () {
      return this.$store.state.activeSchoolYears
    },
    localActivatedSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    schoolYearId () {
      return this.localActivatedSchoolYear.id
    },
    schoolId () {
      return this.$store ? this.$store.getters.schoolId : window.SCHOOL.id
    },
    schoolSettings () {
      return this.$store.state.schoolSettings
    },
    isLoadingSchoolSettings () {
      return this.$store.state.isLoadingSchoolSettings
    },
    initStartPasteDate () {
      const lastIdx = this.daysToCopy.length - 1
      const lastDate = this.daysToCopy[lastIdx]['date']
      // see if it is in the weekend then make first day monday
      if (moment(lastDate).isoWeekday() === 5) {
        return moment(lastDate).add(3, 'days')
      } else if (moment(lastDate).isoWeekday() === 6) {
        return moment(lastDate).add(2, 'days')
      } else {
        return moment(lastDate).add(1, 'days')
      }
    }
  },
  watch: {
    numberOfDays (newValue) {
      const parsedValue = parseInt(newValue, 10)
      if (isNaN(parsedValue)) {
        this.numberOfDays = 1
      } else if (parsedValue > 12) {
        this.numberOfDays = 12
      } else if (parsedValue < 1) {
        this.numberOfDays = 1
      } else {
        this.numberOfDays = parsedValue
      }
    },
    isLoadingSchoolSettings () {
      this.readSavedSourceDates()
    }
  },
  methods: {
    setPasteDates (arr) {
      this.pasteDates = arr
    },
    addDay () {
      if (this.numberOfDays < 12) {
        const lastDate = this.daysToCopy[this.daysToCopy.length - 1]['date']
        const nextDate = moment(lastDate).add(1, 'days').format('YYYY-MM-DD')
        const day = {label: 'Day ' + ((this.daysToCopy.length * 1) + 1), date: nextDate}
        this.daysToCopy.push(day)
        this.numberOfDays = this.daysToCopy.length
      }
    },
    subtractDay () {
      if (this.numberOfDays > 1) {
        this.daysToCopy.pop()
        this.numberOfDays = this.daysToCopy.length
      }
    },
    updateDaysToCopy () {
      const currentDate = this.sourceStartDate
      const newDaysToCopy = []

      for (let i = 0; i < this.numberOfDays; i++) {
        const day = this.daysToCopy[i] || {}
        const label = `Day ${i + 1}`
        const date = day.date || moment(currentDate).add(i, 'days').format('YYYY-MM-DD')
        newDaysToCopy.push({label, date})
      }
      this.daysToCopy = newDaysToCopy
    },
    toggleDropdown () {
      if (!this.isDropdown) {
        this.openDropdown()
      } else {
        this.closeDropdown()
      }
    },
    openDropdown () {
      this.$store.dispatch('readSchoolSettings')
      this.isDropdown = true
    },
    closeDropdown () {
      if (!this.isDropdown) {
        return
      }
      this.isSeeDates = false
      this.isDropdown = false
    },
    setDayToCopyDate (date, index) {
      this.daysToCopy[index].date = moment(date).format('YYYY-MM-DD')
    },
    saveSourceDates () {
      const sourceDates = this.daysToCopy.map(day => day.date)
      const saveObj = {rotating_schedules_source_dates: JSON.stringify(sourceDates)}
      this.saveSchoolYearSetting(saveObj)
    },
    saveSchoolYearSetting (obj) {
      this.$store.dispatch('updateSchoolSettingsForSchoolYear', obj)
    },
    readSavedSourceDates () {
      const settings = this.schoolSettings
      const schoolYearId = this.schoolYearId
      const propSchoolYear = `school_year_${schoolYearId}`
      const propName = 'rotating_schedules_source_dates'
      if (settings && settings[propSchoolYear] && settings[propSchoolYear][propName]) {
        const dates = JSON.parse(settings[propSchoolYear][propName])
        const arr = dates.map((date, i) => {
          return {label: `Day ${i + 1}`, date}
        })
        this.numberOfDays = dates.length
        setTimeout(_ => {
          this.daysToCopy = arr
        }, 0)
      }
    },
    submit () {
      const sourceDates = this.daysToCopy.map(day => day.date)
      this.saveSourceDates()
      const destinationDates = this.pasteDates.filter(day => day.isIncluded).map(day => day.date)
      docendo.calendarCopyDays(this.schoolId, this.calendarId, sourceDates, destinationDates, success => {
        eventbus.toast = {text: 'Inserting days', 'success': true}
        this.closeDropdown()
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
    }
  },
  created () {
    this.updateDaysToCopy()
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.copy-calendars__panel {
  background: #ffffff;
  border-radius: 4px;
  min-width: 800px;
  max-width: 800px;
  width: 800px;
  overflow: hidden;
  box-shadow: 0 3px 8px rgba(0, 0, 0, .24);
  height: auto;
  outline: none;
  text-align: left;
  user-select: none;
}

.copy-rotate__header {
  border-bottom: 1px solid #eceef1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background-color: #f7f8fa;
}

.copy-rotate__body {
  padding: 1rem 2rem;
  max-height: 75vh;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.copy-rotate__footer {
  border-top: 1px solid #eceef1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5rem 1rem;
  background-color: #f7f8fa;
  gap: 1rem;
}

.d-number-picker {
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.d-number-picker input[type="text"] {
  width: 50px;
  text-align: center;
  border: 1px solid #ccc;
  padding: 5px;
  margin: 0;
}

.d-copy-input-group {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.copy-rotate__number-of-days {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;
}

.copy-rotate__number-of-days li {
  min-width: 210px;
  text-align: center;
  border: 1px solid rgb(202 138 4 / 0.2);
  border-radius: 4px;
  padding: 0.5rem 1rem;
  background: #fefce8;
}

.d-circle {
  margin-bottom: 0.5rem;
  background: #fefce8;
  font-size: 10px;
  color: #2b2b28;
  border: 1px solid rgb(202 138 4 / 0.2);
  display: inline-block;
  border-radius: 4px;
  padding: 4px 8px;
  line-height: 10px;
}

</style>



// WEBPACK FOOTER //
// CopyCalendarRotate.vue?f12a2d6a